import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2, Fingerprint, Percent, ShoppingCart, Gift, Ticket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../utils/apiErrors';
import { API_BASE } from '../config/apiBase';
import { isPlatformAuthenticatorAvailable, loginWithBiometric, checkBiometricAvailable } from '../utils/webauthn';
import AnimatedBrandIcon from '../components/AnimatedBrandIcon';

const FLOAT_ICONS = [
    { Icon: Percent, x: '12%', y: '18%', delay: '0s', dur: '6s', size: 18, opacity: 0.13 },
    { Icon: Percent, x: '80%', y: '14%', delay: '1s', dur: '7s', size: 16, opacity: 0.10 },
    { Icon: ShoppingCart, x: '8%', y: '72%', delay: '2s', dur: '8s', size: 20, opacity: 0.09 },
    { Icon: Gift, x: '85%', y: '68%', delay: '0.5s', dur: '7.5s', size: 15, opacity: 0.11 },
    { Icon: Gift, x: '20%', y: '50%', delay: '3s', dur: '6.5s', size: 14, opacity: 0.08 },
    { Icon: Percent, x: '75%', y: '42%', delay: '1.5s', dur: '8s', size: 17, opacity: 0.10 },
    { Icon: ShoppingCart, x: '50%', y: '85%', delay: '4s', dur: '7s', size: 13, opacity: 0.07 },
    { Icon: Percent, x: '35%', y: '10%', delay: '2.5s', dur: '6s', size: 12, opacity: 0.09 },
];

export default function LoginPage() {
    const [isRegister, setIsRegister] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [biometricLoading, setBiometricLoading] = useState(false);
    const [socialProviders, setSocialProviders] = useState({ google: { enabled: true }, facebook: { enabled: false }, apple: { enabled: false } });
    const [formData, setFormData] = useState({ name: '', email: '', password: '' });
    
    const { login, register, loginWithGoogle, loginWithFacebook, setUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        isPlatformAuthenticatorAvailable().then(ok => { if (ok) setBiometricAvailable(true); });
        const loadProviders = async () => {
            try {
                // Tenta endpoint canônico primeiro, fallback para settings se 404
                let resp = await fetch(`${API_BASE}/api/auth/social-providers`);
                if (resp.status === 404) {
                    resp = await fetch(`${API_BASE}/api/settings/social-login`);
                }
                const raw = await resp.text();
                const parsed = raw ? JSON.parse(raw) : {};
                if (parsed && typeof parsed === 'object') setSocialProviders(parsed);
            } catch (_ignored) {
                /* fallback silencioso — mantém defaults */
            }
        };
        loadProviders();
    }, []);

    const handleBiometricLogin = async () => {
        if (!formData.email.trim()) { toast.error('Digite seu email primeiro'); return; }
        setBiometricLoading(true);
        try {
            const hasCredential = await checkBiometricAvailable(formData.email);
            if (!hasCredential) { toast.error('Biometria não cadastrada para este email.'); return; }
            const data = await loginWithBiometric(formData.email);
            setUser(data.user);
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem('token', data.token);
            toast.success('Autenticado com biometria!');
            const role = data.user?.role;
            const acType2 = data.user?.account_type;
            navigate((role === 'admin' || role === 'approved' || ['client','client_user','empresa','colaborador'].includes(acType2)) ? '/sistema' : '/perfil');
        } catch (err) { toast.error(normalizeAppError(err, 'login').message); } finally { setBiometricLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let result;
            if (isRegister) {
                if (!formData.name.trim()) { toast.error('Digite seu nome'); return; }
                result = await register(formData.name, formData.email, formData.password, rememberMe);
                toast.success('Conta criada com sucesso!');
            } else {
                result = await login(formData.email, formData.password, rememberMe);
                toast.success('Bem-vindo de volta!');
            }
            const role = result?.user?.role;
            const acType = result?.user?.account_type;
            if (role === 'admin' || role === 'approved' || acType === 'client' || acType === 'client_user' || acType === 'empresa' || acType === 'colaborador') {
                navigate('/sistema');
            } else { navigate('/perfil'); }
        } catch (error) { toast.error(normalizeAppError(error, isRegister ? 'register' : 'login').message); } finally { setLoading(false); }
    };

    return (
        <div className="sv-login min-h-screen flex" style={{ background: 'var(--login-bg)' }} data-testid="login-page">
            {/* Animations */}
            <style>{`
                @keyframes sv-float { 0%,100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
                @keyframes sv-coupon-float { 0% { transform: translateY(0) rotate(0deg); opacity: 0.07; } 25% { opacity: 0.12; } 50% { transform: translateY(-30px) rotate(5deg); opacity: 0.07; } 75% { opacity: 0.12; } 100% { transform: translateY(0) rotate(0deg); opacity: 0.07; } }
            `}</style>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Cupons flutuando */}
                {[
                    { x: '8%', y: '15%', size: 28, dur: '7s', delay: '0s' },
                    { x: '85%', y: '25%', size: 22, dur: '9s', delay: '2s' },
                    { x: '15%', y: '70%', size: 24, dur: '8s', delay: '1s' },
                    { x: '78%', y: '65%', size: 20, dur: '10s', delay: '3s' },
                    { x: '45%', y: '85%', size: 18, dur: '11s', delay: '4s' },
                    { x: '92%', y: '80%', size: 16, dur: '8.5s', delay: '1.5s' },
                ].map((c, i) => (
                    <div key={`coupon-${i}`} className="absolute" style={{ left: c.x, top: c.y, animation: `sv-coupon-float ${c.dur} ease-in-out ${c.delay} infinite` }}>
                        <Ticket size={c.size} style={{ color: 'var(--sv-accent)' }} />
                    </div>
                ))}
                {FLOAT_ICONS.map((item, i) => (
                    <div key={i} className="absolute" style={{ left: item.x, top: item.y, animation: `sv-float ${item.dur} ease-in-out ${item.delay} infinite`, opacity: item.opacity }}>
                        <item.Icon size={item.size} style={{ color: 'var(--sv-accent)' }} />
                    </div>
                ))}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 relative z-10">
                <div className="sv-login-cartao w-full max-w-sm">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block">
                            <AnimatedBrandIcon size={80} glow={true} />
                        </Link>
                        <h1 className="mt-5 text-xl font-semibold" style={{ color: 'var(--login-texto)', fontFamily: "var(--font-display)", letterSpacing: '-0.01em' }}>
                            {isRegister ? 'Criar Conta' : 'Entrar no SuperVitrine'}
                        </h1>
                        <p className="text-xs mt-1.5" style={{ color: 'var(--login-secundario)' }}>
                            {isRegister ? 'Crie sua conta para publicar ofertas' : 'As melhores ofertas num só lugar'}
                        </p>
                    </div>

                    {/* Social Login */}
                    <div className="flex items-center justify-center gap-3 mb-4">
                        {socialProviders.google?.enabled && socialProviders.google?.configured && (
                        <button onClick={loginWithGoogle}
                            className="w-12 h-12 flex items-center justify-center rounded-xl transition-all hover:scale-[1.06] active:scale-95"
                            style={{ background: 'var(--login-campo)', border: '1px solid var(--login-borda)' }}
                            data-testid="social-google"
                            title="Continuar com Google">
                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        </button>
                        )}
                        {socialProviders.facebook?.enabled && socialProviders.facebook?.configured && (
                        <button onClick={loginWithFacebook}
                            className="w-12 h-12 flex items-center justify-center rounded-xl transition-all hover:scale-[1.06] active:scale-95"
                            style={{ background: 'var(--login-campo)', border: '1px solid var(--login-borda)' }}
                            data-testid="social-facebook"
                            title="Continuar com Facebook">
                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </button>
                        )}
                    </div>

                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid var(--login-borda)' }}></div></div>
                        <div className="relative flex justify-center text-[10px]"><span className="px-3" style={{ background: 'var(--login-fundo-rotulo)', color: 'var(--login-secundario)' }}>ou continue com email</span></div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-3">
                        {isRegister && (
                            <div className="relative">
                                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--login-icone)' }} />
                                <input type="text" placeholder="Seu nome" value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[rgba(var(--sv-accent-rgb),0.35)]"
                                    style={{ background: 'var(--login-campo)', border: '1px solid var(--login-borda-campo)', color: 'var(--login-texto-campo)' }}
                                    data-testid="register-name-input" />
                            </div>
                        )}
                        <div className="relative">
                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--login-icone)' }} />
                            <input type="email" placeholder="Email" value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })} required
                                className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[rgba(var(--sv-accent-rgb),0.35)]"
                                style={{ background: 'var(--login-campo)', border: '1px solid var(--login-borda-campo)', color: 'var(--login-texto-campo)' }}
                                data-testid="email-input" />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--login-icone)' }} />
                            <input type={showPassword ? 'text' : 'password'} placeholder="Senha" value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6}
                                className="w-full rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[rgba(var(--sv-accent-rgb),0.35)]"
                                style={{ background: 'var(--login-campo)', border: '1px solid var(--login-borda-campo)', color: 'var(--login-texto-campo)' }}
                                data-testid="password-input" />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--login-icone)' }}>
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me">
                            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                                className="w-3.5 h-3.5 rounded accent-[var(--sv-accent)]" />
                            <span className="text-xs" style={{ color: 'var(--login-secundario)' }}>Lembrar de mim</span>
                        </label>

                        <Button type="submit" disabled={loading} className="w-full py-3 text-sm font-semibold rounded-xl"
                            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="submit-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRegister ? 'Criar Conta' : 'Entrar')}
                        </Button>

                        {!isRegister && biometricAvailable && /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
                            <button type="button" onClick={handleBiometricLogin} disabled={biometricLoading}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all"
                                style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)', color: 'var(--sv-accent)' }}
                                data-testid="biometric-login-btn">
                                {biometricLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Fingerprint className="w-3.5 h-3.5" />}
                                Entrar com biometria
                            </button>
                        )}
                    </form>

                    <p className="text-center text-xs mt-5" style={{ color: 'var(--login-secundario)' }}>
                        {isRegister ? 'Já tem uma conta?' : 'Não tem uma conta?'}{' '}
                        <button onClick={() => setIsRegister(!isRegister)} className="font-medium hover:underline" style={{ color: 'var(--sv-accent)' }} data-testid="toggle-auth-mode">
                            {isRegister ? 'Entrar' : 'Criar conta'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
